import { forEach, get, cloneDeep, isEmpty } from 'lodash';
import { sectorIndustry } from '../../config/sectorIndustry';
import config from '../../config/config';
import countriesCode from '../../config/countriesCode';
import {
  setIsFilterActive,
  setSelectedFilter,
  setWatchlistType,
  setWatchlistFileType,
  setWatchlistMetric,
  setWatchlistUniverse,
  setSelectedCategoryID,
  setSelectedExchange,
  setCompleteCompaniesData,
} from 'reducers/Watchlist';
import WatchlistService from './WatchlistService';
import { getState } from 'config/store';
import { getItemFromLocalStorage, setItemInLocalStorage } from 'utils/helpers';

const fields10k = {
  totdoc: ['i', 'j', 'k', 'l', 'm', 'n', 'o'],
  mda: ['w', 'x', 'y', 'z', 'aa', 'ab', 'ac'],
  rf: ['ak', 'al', 'am', 'an', 'ao', 'ap', 'aq'],
  notes: ['ay', 'az', 'ba', 'bb', 'bc', 'bd', 'be'],
};

const fields10q = {
  totdoc: ['p', 'q', 'r', 's', 't', 'u', 'v'],
  mda: ['ad', 'ae', 'af', 'ag', 'ah', 'ai', 'aj'],
  rf: ['ar', 'as', 'at', 'au', 'av', 'aw', 'ax'],
  notes: ['bh', 'bi', 'bj', 'bk', 'bl', 'bm', 'bn'],
};

const commonColumns = [
  'sentiment',
  'sentimentWord',
  'sentimentChange',
  'sentimentChangeWord',
  'wordCountChange',
  'wordCountChangePercent',
  'wordCountChangePercentWord',
];

const formatFileTypeData = (fileTypeFields, rawData) => {
  const fileTypeData = {};
  forEach(fileTypeFields, (_metricFields, metricName) => {
    fileTypeData[metricName] = {};
    commonColumns.forEach((fieldKey, index) => {
      fileTypeData[metricName][fieldKey] = get(rawData, fileTypeFields[metricName][index], null);
    });
  });
  return fileTypeData;
};

export const formatFillingsData = rawData => {
  if (!rawData) return {};

  return {
    sScore: rawData.a,
    sScoreText: rawData.b,
    sVolume: rawData.c,
    sVolumeText: rawData.d,
    svScore: rawData.e,
    svScoreText: rawData.f,
    raws: rawData.g,
    rawsScore200: rawData.h,
    sBuzz: rawData.i,
    sDispersion: rawData.j,
    sDispersionText: rawData.k,
  };
};

export const formatData = rawDataArr => {
  return rawDataArr.map(rawData => formatComapnyData(rawData));
};

export const formatComapnyData = rawData => {
  const sectorIndustryObj = sectorIndustry[rawData.cc];
  return {
    isActiveFlag: Boolean(parseInt(get(rawData, 'cq', '0'))),
    ticker: get(rawData, 'ticker', null),
    isTickerActive: get(rawData, 'isTickerActive', false),
    companyName: get(rawData, 'b', null),
    industry: sectorIndustryObj?.industry ?? get(rawData, 'd'),
    sector: sectorIndustryObj?.sector ?? get(rawData, 'c'),
    mktcap: rawData.e,
    adv: rawData.f,
    last10k: rawData.last_10k,
    last10q: rawData.last_10q,
    '10-K': formatFileTypeData(fields10k, rawData),
    '10-Q': formatFileTypeData(fields10q, rawData),
    recentId10k: rawData['bu'],
    recentId10q: rawData['bw'],
    oldId10k: rawData['bv'],
    oldId10q: rawData['bx'],
    periodDate10k: rawData['ca'],
    periodDate10q: rawData['cb'],
    countryCode: get(rawData, 'co', ''),
    countryName: countriesCode.find(c => c.code === rawData['co'])?.name ?? '',
    flag: get(rawData, 'cp', null),
    companyId: rawData.cu,
    sourceName: rawData.type === 'global' ? 'SEDAR' : rawData.type === 'domestic' ? 'SEC' : null,
    marketIndex: rawData.market_index,

    ...formatFillingsData(rawData.cfs),
  };
};

export const FILTERS_KEY = 'watchlist::filtering';

export const getColumnState = keyName => {
  return getItemFromLocalStorage(keyName, []);
};

export const getFilteringState = keyName => {
  return getItemFromLocalStorage(keyName, {});
};

export const storeColumnsState = (keyName, state) => {
  setItemInLocalStorage(keyName, state);
};

export const storeFilteringState = (keyName, state) => {
  setItemInLocalStorage(keyName, state);
};

export const getWatchlistSettings = () => {
  return getItemFromLocalStorage('watchlistSetting', {});
};
export const saveWatchlistSettings = setting => {
  setItemInLocalStorage('watchlistSetting', setting);
};

export const isItSocialCompany = flag => {
  const socialCompanyFlags = [2, 6, 13, 15];
  if (flag) {
    flag = parseInt(flag);
    return socialCompanyFlags.includes(flag);
  } else {
    return false;
  }
};

export const isActive = (path, selectedItem) => {
  if (!selectedItem || !path) return false;
  /*
      Comparison button must be disabled for AR(Annual report) document type
      Comparison button will also be disabled if both pairs of IDs are missing.
      pair_1 = recentId10k, oldId10k
      pair_2 = recentId10q, oldId10q
  */
  if (path === '/filings' && selectedItem.companyId) {
    return true;
  }
  if (
    path === '/comparision' &&
    ((selectedItem.recentId10k && selectedItem.oldId10k) || (selectedItem.recentId10q && selectedItem.oldId10q)) &&
    selectedItem.documentType?.toUpperCase() !== 'AR' &&
    selectedItem.documentType?.toUpperCase() !== 'ANNUAL REPORT'
  ) {
    return true;
  }
  if ((path === '/news' || path === '/socialSentiment') && isItSocialCompany(selectedItem.flag)) {
    return true;
  }
  if (path === '/sentiment' && selectedItem.recentId) {
    return true;
  }

  return false;
};

export const getCompanyFromGlobalStatesByCompanyId = companyId => {
  return (dispatch, getState) => {
    if (!companyId) return;
    try {
      const { completeCompaniesData, completeCompaniesDataGlobal } = getState().Watchlist;
      let company = completeCompaniesData.find(a => a.companyId === companyId);
      if (company) {
        return company;
      } else {
        company = completeCompaniesDataGlobal.find(a => a.companyId === companyId);
        if (company) {
          return company;
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  };
};

export const getGridDataWithNewTickerStatus = (gridData, companyId) => {
  return gridData.map(company =>
    company.companyId === companyId ? { ...company, isTickerActive: !company.isTickerActive } : company
  );
};

export const updateCompleteCompaniesData = companyId => {
  return (dispatch, getState) => {
    const completeCompaniesData = getState().Watchlist.completeCompaniesData;
    const updatedCompaniesData = getGridDataWithNewTickerStatus(completeCompaniesData, companyId);
    dispatch(setCompleteCompaniesData(updatedCompaniesData));
  };
};

export const clearFilterHandler = () => {
  return async dispatch => {
    storeFilteringState(FILTERS_KEY, null);
    WatchlistService.clearFilter();
    dispatch(setSelectedFilter(null));
    dispatch(setIsFilterActive(false));
  };
};

export const isBigAgGrid = selectedFileType => {
  return selectedFileType === '10-K' || selectedFileType === '10-Q';
};

export const getFilterCountriesArray = filterString => {
  if (filterString) {
    let countryiesArray = countriesCode
      .filter(countryObj => countryObj.name.toLowerCase().includes(filterString.toLowerCase()))
      .map(countryObj => countryObj.code)
      .join(',');
    return countryiesArray;
  } else {
    return '';
  }
};

export const getTickerLogo = selectedItem => {
  const companyId = get(selectedItem, 'companyId', '');
  const tickerUrl = `${config.companyLogoPath}${companyId}.png`;
  return tickerUrl;
};

export const setFilters = filter => {
  return dispatch => {
    if (!isEmpty(filter.search_json)) {
      let filtersCopy = cloneDeep(filter.search_json);
      if (filtersCopy) {
        dispatch(setWatchlistType(filtersCopy.selectedType ?? 'domestic'));
        dispatch(setWatchlistFileType(filtersCopy.selectedFileType ?? 'all'));
        dispatch(setWatchlistMetric(filtersCopy.selectedMetric ?? 'totdoc'));
        if (filtersCopy.selectedUniverse && filtersCopy.selectedUniverse === 'watchlist') {
          dispatch(setWatchlistUniverse('watchlist'));
        } else {
          dispatch(setWatchlistUniverse('all'));
        }
        if (filtersCopy.selectedUniverse === 'watchlist') {
          dispatch(setSelectedCategoryID(filtersCopy.selectedCategoryID ?? null));
        }
        if (filtersCopy.selectedUniverse === 'all') {
          dispatch(setSelectedExchange(filtersCopy.selectedExchange ?? 'all'));
        }
      }
      dispatch(setSelectedFilter({ id: filter.id, filterLabel: filter.filter_label }));
      setTimeout(() => {
        dispatch(setIsFilterActive(true));
        delete filtersCopy['selectedType'];
        delete filtersCopy['selectedFileType'];
        delete filtersCopy['selectedMetric'];
        delete filtersCopy['selectedUniverse'];
        delete filtersCopy['selectedCategoryID'];
        delete filtersCopy['selectedExchange'];
        WatchlistService.agGridAPI.setFilterModel(filtersCopy);
      }, 1500);
    }
  };
};

export const getWatchlistTickersByCategoryId = categoryId => {
  const watchlistCategories = getState().Watchlist.watchlistCategories;

  const selectedCategory = watchlistCategories.find(category => category.id === categoryId);

  return selectedCategory?.watchlists?.length ? selectedCategory.watchlists.map(s => s.ticker) : [];
};
